import {
  Button,
  Checkbox,
  createStyles,
  FormControlLabel,
  IconButton,
  ListItem,
  ListItemText,
  makeStyles,
  Menu,
  MenuItem,
  Theme,
  Typography,
} from '@material-ui/core'
import { MoreVertOutlined } from '@material-ui/icons'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import {
  RemoveAllWorkingStepsNotAvailableInPortal,
  RemoveRowsByIssueCode,
  SolveAllIssuesByIssueCodeAsync,
} from 'features/BillOfMaterials/store/asyncActions/SolveIssueAsync'
import { newProjectActions } from 'features/BillOfMaterials/store/newProjectReducer'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import _, { isEqual } from 'lodash'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store/configureStore'
import { useAppDispatch } from 'store/configureStore'
import {
  IssueCode,
  IssueDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { RootAppState } from 'store/configureStore'
import { BoMItemFilterCount } from '../BoMItemFilterCounter/BoMItemFilterCounter'
import { useEnabledBoMFilters } from '../useEnabledBoMFilters'
import './IssuesFilterContainer.scss'

const groupIssues = (issues: IssueDto[], groupBy: keyof IssueDto) =>
  _.groupBy(issues, (x) => x[groupBy])

const selectIssueFilter = (state: RootAppState) => ({
  active: state.project.filters.byIssues.active,
  enabledFilters: state.project.filters.byIssues.filter.enabledFilters,
  availableFilters: state.project.filters.byIssues.filter.availableFilters,
})

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    listItem: {
      cursor: 'pointer',
      width: '100%',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      textTransform: 'lowercase',
      justifyContent: 'space-between',
    },
    title: {
      padding: theme.spacing(0, 0, 1, 2),
      width: '100%',
      display: 'block',
      borderBottom: `1px solid ${theme.palette.primary.main}`,
    },
  })
)

const IssueItemMenu = (props: { issueCode: IssueCode; isBuyer: boolean }) => {
  let menuItems = []
  const { t } = useTranslation()

  const dispatch = useAppDispatch()

  const menuActionHandler = (e, eventKey: string) => {
    e.preventDefault()
    e.stopPropagation()

    switch (eventKey) {
      case 'allow-all-working-steps': {
        dispatch(SolveAllIssuesByIssueCodeAsync({ issueCode: props.issueCode }))
        break
      }
      case 'remove-all-not-allowed-working-steps': {
        dispatch(RemoveAllWorkingStepsNotAvailableInPortal())
        break
      }
      case 'remove-all-parts-not-available': {
        dispatch(RemoveRowsByIssueCode({ issueCode: props.issueCode }))
        break
      }
    }
  }

  switch (props.issueCode) {
    case IssueCode.WorkingStepNotAvailable: {
      menuItems = [
        !props.isBuyer ? (
          <MenuItem
            key="allow-all-working-steps"
            onClick={(e) => menuActionHandler(e, 'allow-all-working-steps')}
          >
            {t(
              'project:allow-all-working-steps-from-portal',
              'allow all working steps from portal'
            )}
          </MenuItem>
        ) : null,
        <MenuItem
          key="remove-all-not-allowed-working-steps"
          onClick={(e) =>
            menuActionHandler(e, 'remove-all-not-allowed-working-steps')
          }
        >
          {t(
            'project:remove-all-not-available-working-steps',
            'remove all not available working steps'
          )}
        </MenuItem>,
        !props.isBuyer ? (
          <MenuItem
            key="remove-all-parts-not-available"
            onClick={(e) =>
              menuActionHandler(e, 'remove-all-parts-not-available')
            }
          >
            {t(
              'project:remove-all-not-available-parts',
              'remove all parts with not available working steps'
            )}
          </MenuItem>
        ) : null,
      ]
    }
  }

  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  if (!menuItems?.length) {
    return null
  }

  return (
    <>
      <IconButton
        size="small"
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation()
          setOpen(true)
        }}
      >
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={buttonRef.current}
        open={open}
        onClose={() => setOpen(false)}
      >
        {menuItems}
      </Menu>
    </>
  )
}

const IssueItem = (props: {
  onClick: (e) => void

  checked: boolean
  issueCount: number
  issueCode: IssueCode
  issueSeverity: IssueDto['severity']
  className: string
}) => {
  const { t } = useTranslation()

  const isBuyer = useAppSelector(projectSelectors.ProjectInBuyerViewSelector)

  return (
    <>
      <ListItem className={props.className}>
        <FormControlLabel
          style={{ flex: '1 1 100%' }}
          onClick={props.onClick}
          control={<Checkbox checked={props.checked} color="primary" />}
          label={
            <ListItemText
              primary={
                <>
                  <Typography component="span">
                    {t(`project:issues-${props.issueCode}`, props.issueCode)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    component="span"
                  >
                    {` (${props.issueCount})`}
                  </Typography>
                </>
              }
              secondary={t(`common:${props.issueSeverity}`)}
            />
          }
        />
        <IssueItemMenu issueCode={props.issueCode} isBuyer={isBuyer} />
      </ListItem>
    </>
  )
}

export const IssuesFilterContainer = (props: {
  showTitle?: boolean
  hideNoneAndAllButtons?: boolean
}) => {
  const { t } = useTranslation()
  const cn = useStyles()

  const dispatch = useAppDispatch()
  const { active, availableFilters, enabledFilters } = useAppSelector(
    selectIssueFilter,
    isEqual
  )

  const {
    workingStepsFilterEnabled,
    materialFilterEnabled,
    issueFilterEnabled,
  } = useEnabledBoMFilters()

  const issueCodes = groupIssues(availableFilters as IssueDto[], 'issueCode')

  // const issuesSummary = groupIssues(availableFilters.filter(x => enabledFilters.length ? enabledFilters.includes(x.issueCode) : true) as IssueDto[], 'severity')

  const handleChange = (e, issueCode: IssueCode, checked: boolean) => {
    e.preventDefault()
    e.bubble = false

    const newEnabledFilters = checked
      ? [...(enabledFilters || []), issueCode]
      : enabledFilters.filter((x) => x !== issueCode)

    dispatch(
      newProjectActions.setFilter({
        filter: {
          type: 'IssuesFilter',
          enabledFilters: newEnabledFilters,
        },
      })
    )
  }

  const selectAllOrNothing = (howMany: 'all' | 'nothing') => {
    dispatch(
      newProjectActions.setFilter({
        filter: {
          type: 'IssuesFilter',
          enabledFilters:
            howMany === 'all' ? (Object.keys(issueCodes) as IssueCode[]) : [],
        },
      })
    )
  }

  if (_.isEmpty(issueCodes)) {
    return (
      <div className="filter-empty">
        <LocalizedTypography
          translationKey="common:empty"
          color="textSecondary"
        >
          empty
        </LocalizedTypography>
      </div>
    )
  }

  return (
    <div className="filters-container">
      <div className="issues-filter-container">
        {props.showTitle && (
          <>
            <BoMItemFilterCount
              workingStepFilterEnabled={workingStepsFilterEnabled}
              materialFilterEnabled={materialFilterEnabled}
              issuesFilterEnabled={issueFilterEnabled}
            />
            <LocalizedTypography
              translationKey={'common:issues'}
              variant="body2"
              color="primary"
              className={cn.title}
            >
              issues
            </LocalizedTypography>
          </>
        )}
        <ul className="issues-filter-code-list">
          {Object.keys(issueCodes).map((issueCode) => {
            return (
              <IssueItem
                key={issueCode}
                onClick={(e) =>
                  handleChange(
                    e,
                    issueCode as never,
                    !(enabledFilters?.findIndex((x) => x === issueCode) > -1)
                  )
                }
                checked={enabledFilters?.findIndex((x) => x === issueCode) > -1}
                issueCount={issueCodes[issueCode].length}
                issueCode={issueCode as IssueCode}
                issueSeverity={
                  availableFilters.find((x) => x.issueCode === issueCode)
                    .severity
                }
                className={cn.listItem}
              />
            )
          })}
        </ul>
      </div>
      {!props.hideNoneAndAllButtons && (
        <div className="filter-actions">
          {active && enabledFilters?.length === 0 ? (
            <LocalizedTypography
              translationKey={'project:filter-empty-issues'}
              variant="caption"
              color="secondary"
            >
              filtering parts without any issues
            </LocalizedTypography>
          ) : null}

          <Button
            variant="outlined"
            color="primary"
            onClick={() => selectAllOrNothing('nothing')}
          >
            {t('common:none')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => selectAllOrNothing('all')}
          >
            {t('common:all')}
          </Button>
        </div>
      )}
    </div>
  )
}
